export const ROLE_CAPS = {
    admin: { advance: true, approve: true, submit: true, editModel: true, manageUsers: true, manageVault: true, overrideSoD: true },
    partner: { advance: true, approve: true, submit: true, editModel: true, manageUsers: false, manageVault: false, overrideSoD: false },
    analyst: { advance: true, approve: false, submit: true, editModel: false, manageUsers: false, manageVault: false, overrideSoD: false },
    readonly: { advance: false, approve: false, submit: false, editModel: false, manageUsers: false, manageVault: false, overrideSoD: false },
};
// Ordered capabilities + labels, for the admin per-user permissions editor.
export const CAPABILITIES = [
    { key: "advance", label: "Advance", description: "Move records through pipeline stages" },
    { key: "approve", label: "Approve", description: "Approve matches / confirm human-gate decisions" },
    { key: "submit", label: "Submit", description: "Submit records for matching / create documents" },
    { key: "editModel", label: "Edit Model", description: "Edit the process model; toggle & sync connectors" },
    { key: "manageUsers", label: "Manage Users", description: "View the audit log; manage users & permissions" },
    { key: "manageVault", label: "Manage Vault", description: "Set, rotate, and delete vault credentials" },
    { key: "overrideSoD", label: "Override SoD", description: "Override separation-of-duties checks (reserved)" },
];
// Effective capabilities = role baseline with any per-user overrides applied.
// This is the single source of truth the server enforces and the client displays.
export function effectiveCaps(role, overrides) {
    return { ...ROLE_CAPS[role], ...(overrides ?? {}) };
}
export function hasCap(role, overrides, cap) {
    return effectiveCaps(role, overrides)[cap];
}
export const ORGANIZATIONS = [
    { key: "vets", label: "VETS", description: "VETS staff. Super admin: sees and can do everything." },
    { key: "r3x", label: "R3x", description: "R3x staff. Sees all data, with the role you give them." },
    { key: "company", label: "Company", description: "Someone at a portfolio company. Sees only the companies assigned to them." },
];
export const VETS_EMAIL_DOMAIN = "vetsllc.org";
// A user whose organization was never set counts as VETS when their email is
// a VETS address, and as R3x otherwise, so nobody loses access on upgrade.
export function effectiveOrganization(org, email) {
    if (org === "vets" || org === "r3x" || org === "company")
        return org;
    return email.trim().toLowerCase().endsWith(`@${VETS_EMAIL_DOMAIN}`) ? "vets" : "r3x";
}
export function effectiveAccess(role, overrides, org) {
    if (org === "vets")
        return { role: "admin", caps: { ...ROLE_CAPS.admin }, superAdmin: true };
    if (org === "company") {
        const r = role === "readonly" ? "readonly" : "analyst";
        return {
            role: r,
            caps: { ...effectiveCaps(r, overrides), advance: false, approve: false, editModel: false, manageUsers: false, manageVault: false, overrideSoD: false },
            superAdmin: false,
        };
    }
    return { role, caps: effectiveCaps(role, overrides), superAdmin: false };
}
export const segmentOfKind = (kind) => (kind === "portfolio" ? "onboarded" : "potential");
export const NOTIFICATION_EVENTS = [
    { key: "match_confirmed", label: "Match confirmed", description: "A partner confirmed a company ↔ opportunity pairing." },
    { key: "opportunity_ingested", label: "New opportunities", description: "A connector sync ingested new opportunities." },
    { key: "feedback_updated", label: "Feedback updates", description: "A bug or improvement changed status." },
];
export const DEFAULT_NOTIFICATION_PREFS = {
    email: true, frequency: "immediate",
    events: { match_confirmed: true, opportunity_ingested: true, feedback_updated: true },
};
// Merge stored prefs (which may be {}) over the defaults — single source of truth.
export function resolveNotificationPrefs(raw) {
    return {
        email: raw?.email ?? DEFAULT_NOTIFICATION_PREFS.email,
        frequency: raw?.frequency ?? DEFAULT_NOTIFICATION_PREFS.frequency,
        events: { ...DEFAULT_NOTIFICATION_PREFS.events, ...(raw?.events ?? {}) },
    };
}
export function wantsNotification(prefs, event) {
    return prefs.email && (prefs.events[event] ?? false);
}
export const DEFAULT_DASHBOARD_LAYOUT = {
    order: ["kpis", "needsToday", "matchDonut", "stageFunnel", "gateQueue", "loiClocks", "actionQueue"],
    widgets: {
        kpis: { visible: true, size: "full", sectorFilter: null },
        needsToday: { visible: true, size: "full", sectorFilter: null },
        matchDonut: { visible: true, size: "half", sectorFilter: null },
        stageFunnel: { visible: true, size: "half", sectorFilter: null },
        // Opt-in extras — the detail behind "Needs you today", for anyone who wants
        // the full queues on the board rather than one click away.
        gateQueue: { visible: false, size: "half", sectorFilter: null },
        loiClocks: { visible: false, size: "half", sectorFilter: null },
        actionQueue: { visible: false, size: "half", sectorFilter: null },
    },
};
export const DEFAULT_EXECUTIVE_LAYOUT = {
    order: ["portfolioKpis", "companyProgress", "sectorMix", "capitalProposals", "topScores", "pinnedCompanies"],
    widgets: {
        portfolioKpis: { visible: true, size: "full", sectorFilter: null },
        companyProgress: { visible: true, size: "full", sectorFilter: null },
        sectorMix: { visible: true, size: "half", sectorFilter: null },
        capitalProposals: { visible: true, size: "half", sectorFilter: null },
        topScores: { visible: false, size: "half", sectorFilter: null },
        pinnedCompanies: { visible: false, size: "half", sectorFilter: null },
    },
};
// Merge a stored layout (which may be {}, or may predate the catalog split)
// over a board's defaults. Two properties matter:
//   • ids the board no longer owns are dropped — an old shared-catalog layout
//     degrades to the new defaults instead of erroring;
//   • ids the board gained land at the END of an existing user's order, so a
//     widget added in a later release appears without resetting their layout.
export function resolveWidgetLayout(raw, defaults) {
    const widgets = { ...defaults.widgets };
    for (const id of defaults.order) {
        const stored = raw?.widgets?.[id];
        if (stored)
            widgets[id] = { ...defaults.widgets[id], ...stored };
    }
    const storedOrder = (raw?.order ?? []).filter((id) => id in widgets);
    const order = [...storedOrder, ...defaults.order.filter((id) => !storedOrder.includes(id))];
    return { order, widgets };
}
/** Back-compat wrapper — the Dashboard board's resolver. */
export function resolveDashboardLayout(raw, defaults = DEFAULT_DASHBOARD_LAYOUT) {
    return resolveWidgetLayout(raw, defaults);
}
export const FEEDBACK_CRITICALITIES = ["low", "medium", "high", "critical"];
export const FEEDBACK_STATUSES = ["new", "triaged", "in_progress", "resolved", "closed"];
export const PROPOSAL_TRACK_STATUSES = [
    { key: "in_evaluation", label: "In evaluation", blurb: "Submitted and with the government evaluators." },
    { key: "clarifications", label: "Clarifications / ENs", blurb: "The government asked questions or opened evaluation notices." },
    { key: "orals", label: "Orals / demo", blurb: "Oral presentation or demonstration scheduled or done." },
    { key: "fpr", label: "Final proposal revision (FPR / BAFO)", blurb: "Discussions held; final revised proposal requested or sent." },
    { key: "awarded", label: "Awarded (won)", blurb: "Contract awarded to our team." },
    { key: "lost", label: "Lost", blurb: "Award went to another offeror. Ask for a debrief." },
    { key: "cancelled", label: "Cancelled", blurb: "The government cancelled or withdrew the solicitation." },
];
export const REPORT_TYPES = [
    { key: "arcline_report", code: "AR", label: "ArcLine Report", description: "The full R3X ArcLine opportunity assessment (Word), built to the R3X ArcLine skill: the opportunity at a glance, board questions, the five track register with fit scores and time to prepare, and a plain language glossary.", generatedFrom: "ArcLine Reports page, full package" },
    { key: "arcline_deck", code: "AD", label: "ArcLine Summary Deck", description: "The summary PowerPoint of an ArcLine Report, built from the finished report.", generatedFrom: "ArcLine Reports page, full package" },
    { key: "arcline_letter", code: "AL", label: "ArcLine Cover Letter", description: "The one page cover letter that goes with the full package.", generatedFrom: "ArcLine Reports page, full package" },
    { key: "arcline_register", code: "AB", label: "ArcLine Bid Decision Register", description: "The Excel bid decision register: one row per opportunity with a live days left count and a Bid, No bid or Watch choice.", generatedFrom: "ArcLine Reports page, full package" },
    { key: "arcline_brief", code: "AI", label: "ArcLine Internal Capture Brief", description: "For VETS only: missing inputs, likely disputes, fee questions and follow ups. Never shown to company users.", generatedFrom: "ArcLine Reports page, full package", internal: true },
    { key: "arcline_teaser", code: "AT", label: "ArcLine Preview Deck", description: "The preview before an agreement: counts, categories and dollar ranges only, never which opportunities.", generatedFrom: "ArcLine Reports page, preview" },
    { key: "opportunity_map", code: "OM", label: "USG Opportunity Map", description: "VETS-branded Word map of the company's federal opportunity space (QA-gated draft).", generatedFrom: "Company page, Opportunity Map button" },
    // Older report kinds, kept so their history stays readable. No longer made.
    { key: "capture_report", code: "CR", label: "Capture Package: Assessment Report (older)", description: "Full Word assessment from the ArcLine Reports service, before ArcLine Reports were combined.", generatedFrom: "No longer made", legacy: true },
    { key: "capture_deck", code: "CD", label: "Capture Package: Internal Deck (older)", description: "Internal summary deck from the ArcLine Reports service, before ArcLine Reports were combined.", generatedFrom: "No longer made", legacy: true },
    { key: "capture_scope", code: "CS", label: "Capture Package: Client Scope Summary (older)", description: "Client scope summary from the ArcLine Reports service, before ArcLine Reports were combined.", generatedFrom: "No longer made", legacy: true },
    { key: "ai_assessment", code: "AA", label: "AI Company Assessment (older)", description: "Short AI-written assessment. Replaced by the ArcLine Report.", generatedFrom: "No longer made", legacy: true },
    { key: "opportunity_analysis", code: "OA", label: "Opportunity Analysis (older)", description: "Opportunity fit write-up. Replaced by the ArcLine Report.", generatedFrom: "No longer made", legacy: true },
];
// Report types company users never see (VETS internal files).
export const INTERNAL_REPORT_TYPES = new Set(REPORT_TYPES.filter((t) => t.internal).map((t) => t.key));
export const reportTypeOf = (key) => REPORT_TYPES.find((t) => t.key === key) ?? { key, code: key.slice(0, 2).toUpperCase(), label: key, description: "", generatedFrom: "" };
// Fit Score v3 — criteria, codes, profile and record types.
export * from "./fit.js";
// Acronyms spelled out (Glossary page, help text, Copilot guide).
export * from "./glossary.js";
// Prime readiness checklist (registrations, clearances, systems).
export * from "./readiness.js";
